import * as $ from "jquery";
import * as Backbone from "backbone";
import * as Marionette from "backbone.marionette";
import * as _ from "underscore";
import { on, Options } from "../utils/decorators";
import { StripAlphaCharacters } from "../utils/currency-field";
import 'url-polyfill';
import Vue from 'vue';

/*
    if you need to add a new field to some membership object, then
        a) add it to the `$db` array in the php class
        b) add it to the `toObject()` array in the php class
        c) add it to the right interface here
*/

interface MembershipGroup {
    id: number;
    name: string;
    description: string;
    descriptionMarkdown: string;
    hasFixedPriceTiers: boolean,
    levels: MembershipLevel[],
    benefits: MembershipBenefit[],
    categories: MembershipCategory[],
    order: number,
    ctaCopy: string,
    memberName: string,
    defaultFundID: number,
};

interface MembershipLevel {
    id: number,
    name: string,
    price: number,
    priceString: string,
    period: string,
    periodString: string,
    fundID: string,
    benefits: {id: number, valueOverride: string}[],
}

interface MembershipBenefit {
    id: number,
    description: string,
    descriptionMarkdown: string,
    content: string,
    contentMarkdown: string,
    order: number,
    categories: number[],
}

interface MembershipCategory {
    id: number,
    name: string,
    upsellText: string,
}


@Options({
    template: false,
    ui: {}
})
export default class MembershipPageView extends Marionette.View<Backbone.Model>{
    config?: object;
    element?: any;
    membershipsData?: MembershipGroup[];
    siblingsData?: any;
    isInputActive?: boolean;
    minimumDonation?: number;

    constructor(options?: object, config?: object) {
        super(options);
        this.config = config || {};
        this.element = this.$el.get(0);

        // get membership & page structure data which was supplied by silverstripe
        try {
            this.membershipsData = (<any>window).memberships_data;
            this.siblingsData = (<any>window).siblings_data;
        } catch (e) {
            console.warn('Error retrieving memberships data.');
            console.warn(e);
        }
    }

    onRender() {
        if(this.membershipsData.length == 0) {
            console.warn('No membership groups found.');
            return;
        }

        // get query params, in case we have preset data
        const urlParams = new URLSearchParams(window.location.search);
        const groupParam = urlParams.get('group');
        const priceParam = urlParams.get('price');

        // create vue view
        let app = new Vue({
            el: this.element,
            data: {
                // data on what other pages exist here in the hierarchy
                siblingPages: this.siblingsData,
                // memberships information (list of groups)
                memberships: this.membershipsData,

                // the ID of the current 'page' (membershipGroup)
                currentGroupID: groupParam == null ? this.membershipsData[0].id : parseInt(groupParam),

                // currently selected price. changing this will select the corresponding level
                price: priceParam == null ? 0 : parseInt(priceParam),

                // has the user clicked the 'filter benefits' button to show the filters list
                filtersOpen: false,
                // has the user clicked 'other amount' to open the CTA
                forceShowCTA: false,
                // has the user focused the CTA input
                ctaInputFocused: false,

                // has the page loaded
                loaded: false,

                isInputActive: false,
                minimumDonation: 10
            },

            // you can use anything in here as a variable here in the code or in the front-end
            // template, and it will update automatically when the data changes
            computed: {
                // get the currently selected group
                group: function() {
                    return this.memberships.find(x => x.id == this.currentGroupID);
                },

                isDisabled() {
                    return this.price < this.minimumDonation;
                },

                // get a list of row data
                // this is kinda complicated, but is used to display the table rows
                // without calling a bunch of expensive functions
                rows: function() {
                    // make a data structure of a list of benefits along with a list of their values in the grid

                    let benefits = this.group.benefits;

                    // if theres no categories, then display everything, otherwise...
                    // get benefits where at least one of its categories is selected
                    if(this.group.categories.length > 0 && !this.group.categories.every(category => category.selected == false)) {
                        benefits = this.group.benefits.filter(benefit => {
                            if(benefit.categories.length == 0) return false;

                            return benefit.categories.some(categoryID => {
                                return this.selectedCategories.some(category => {
                                    return category.id == categoryID
                                })
                            });
                        });
                    }

                    benefits = benefits.filter(benefit => {
                        for(let level of this.group.levels) {
                            if(level.benefits.some(b => b.id == benefit.id)) return true
                        }

                        return false;
                    });



                    let v = benefits.map(benefit => {
                        let useSmallFontSize = false;
                        let benefitsValues = this.group.levels.map(level => {
                            let b = level.benefits.find(x => x.id == benefit.id);
                            let r = {
                                level: level,
                                hasValue: false,
                                hasValueOverride: false,
                                valueOverride: null,
                            };

                            if(b) {
                                r.hasValue = true;
                                r.hasValueOverride = b.valueOverride ? true : false;
                                r.valueOverride = b.valueOverride;
                            }

                            if(r.hasValueOverride && r.valueOverride.length > 10) {
                                useSmallFontSize = true;
                            }

                            return r;
                        });

                        return {
                            benefit: benefit,
                            values: benefitsValues,
                            useSmallFontSize: useSmallFontSize,
                        };
                    });

                    return v;
                },

                // get a list of selected categories
                selectedCategories: function() {
                    return this.group.categories.filter(category => category.selected);
                },

                // get the next group in the list
                nextGroup: function() {
                    let index = this.memberships.findIndex(x => x.id == this.currentGroupID);

                    index++;

                    if(index < this.memberships.length) {
                        return this.memberships[index];
                    }

                    return null;
                },

                // get a list of all levels in all groups (with the same), sorted by price
                allLevels: function() {
                    let ret = [];

                    for(let group of this.memberships) {
                        if(this.group.hasFixedPriceTiers) {
                            if(group.id == this.group.id) {
                                ret = ret.concat(group.levels);
                            }
                        } else {
                            if(!group.hasFixedPriceTiers) {
                                ret = ret.concat(group.levels);
                            }
                        }
                    }

                    ret.sort((a,b) => a.price - b.price);

                    return ret;
                },

                // get the selected level, given the current price
                selectedLevel: function() {
                    let levels = this.allLevels;
                    for(let i = levels.length - 1; i >= 0; i--) {
                        if (levels[i].price <= this.price) {
                            return levels[i];
                        }
                    }

                    return null
                },

                // get the group which contains the currently selected level, given the current price
                groupContainingSelectedLevel() {
                    if(this.selectedLevel == null) {
                        return null;
                    }

                    for(let group of this.memberships) {
                        if (group.levels.find(x => x.id == this.selectedLevel.id)) {
                            return group;
                        }
                    }

                    return null;
                },

                // return whether the current group contains the selected level
                // (so we can link to the right group if not)
                isSelectedLevelInCurrentGroup() {
                    let group = this.groupContainingSelectedLevel;

                    if(group == null) return false;

                    if(group.id == this.currentGroupID) return true;

                    return false;
                },

                // create a list of nav links, including both sibling pages and also
                // links to different groups
                navLinks() {
                    let arr = [];

                    // create links to all membership groups
                    for(let group of this.memberships) {
                        let newurl = this.getURLOfGroup(group.id);

                        arr.push({
                            title: group.name,
                            link: newurl,
                            selected: this.currentGroupID == group.id,
                        });
                    }

                    // create links to all sibling pages
                    for(let sibling of this.siblingPages) {
                        arr.push({
                            title: sibling.title,
                            link: sibling.link,
                            selected: false,
                        });
                    }

                    return arr;
                },

                // for mobile, handle showing the right select option for nav
                currentSelectOption: {
                    get() {
                        return this.getURLOfGroup(this.currentGroupID);
                    },

                    set(value) {
                        (<any>window.location.href) = value;
                    }
                },

                buyURL() {
                    let domain = window['DJANGO']['boDomain'];

                    domain += '/donate/';

                    if(this.selectedLevel) {
                        domain += this.selectedLevel.fundID;
                    } else {
                        domain += this.group.defaultFundID || 0;
                    }

                    domain += '/add-to-basket/';
                    domain += this.price;

                    return domain;
                },

                priceString: {
                    get() {
                        if (this.isInputActive) {
                            // Cursor is inside the input field. unformat display value for user
                            return this.price.toString()
                        } else {
                            // User is not modifying now. Format display value for user interface
                            if (this.price <= 0) {
                                return '';
                            } else {
                                return this.price.toLocaleString()
                            }
                        }
                    },
                    set(modifiedValue: string) {
                        this.price  = StripAlphaCharacters(modifiedValue);
                    }
                },

                startingPriceOfCurrentGroup() {
                    let levels = this.group.levels;

                    if(levels.length) {
                        return levels[0].price;
                    }

                    return null;
                },

                shouldShowInput() {
                    if(!this.group.hasFixedPriceTiers) {
                        return true;
                    }

                    if(this.price < this.startingPriceOfCurrentGroup) {
                        return true;
                    }

                    return false;
                }
            },

            methods: {
                // sorts memebrship groups, benefits and levels and also
                // preset some data needed for display
                sortData() {
                    this.memberships.sort((a,b) => a.order - b.order);
                    for(let group of this.memberships) {
                        group.benefits.sort((a,b) => a.order - b.order);
                        group.levels.sort((a,b) => a.price - b.price);

                        for(let level of group.levels) {
                            level.id = group.id + '_' + level.id;
                        }

                        for(let benefit of group.benefits) {
                            Vue.set(benefit, 'expanded', false);
                        }

                        for(let category of group.categories) {
                            Vue.set(category, 'selected', false);
                        }
                    }
                },

                // select a level
                select(level) {
                    this.price = level.price;
                    this.updateCTABodyClass();
                },

                // get whether a given level is the currently selected level
                isSelected(level) {
                    return this.selectedLevel ? level.id == this.selectedLevel.id : false;
                },

                // get a link to this membership page, with the query params set
                // to display the given membership group
                getURLOfGroup(id) {
                    let searchParams = new URLSearchParams(window.location.search);
                    searchParams.set('group', id);
                    if(this.price) {
                        searchParams.set('price', this.price);
                    }
                    let newurl = window.location.protocol + "//" + window.location.host + window.location.pathname + '?' + searchParams.toString();

                    return newurl;
                },

                deselectAllFilters() {
                    this.group.categories.forEach(filter => filter.selected = false);
                },

                selectFilter(filter) {
                    this.group.categories.forEach(filter => filter.selected = false);
                    filter.selected = true;
                },

                // force displayed the CTA, regardless of price
                showCTA() {
                    if(this.group.hasFixedPriceTiers) {
                        this.price = 0;
                    }

                    this.forceShowCTA = true;
                    this.updateCTABodyClass();
                },

                // applies a class to the body if the CTA is shown
                updateCTABodyClass() {
                    let className = 'membership-cta-shown';
                    setTimeout(() => {
                        let privacy = (<any>document.querySelector('.privacy-popup'));
                        let offset = 0
                        if(this.price > 0 || this.forceShowCTA) {
                            offset = document.querySelector('.membership-cta').getBoundingClientRect().height;
                            document.body.classList.add(className);
                        } else {
                            document.body.classList.remove(className);
                        }

                        if(privacy) {
                            if(offset == 0) {
                                privacy.style.transform = `translate(-50%, 0px)`;
                            } else {
                                privacy.style.transform = `translate(-50%, -${offset}px)`;
                            }
                        }
                    })
                },

                updateScroll(event) {
                    let scrollElements = document.querySelectorAll('.match-scroll');

                    for(let i = 0; i<scrollElements.length; i++) {
                        let element = scrollElements[i];

                        if(element == event.currentTarget) continue;

                        element.scrollLeft = event.currentTarget.scrollLeft;
                    }
                },

                inputKeypress(event) {
                    if(event.key == 'ArrowUp') {
                        this.price += 5;
                        return false;
                    }

                    if(event.key == 'ArrowDown') {
                        this.price -= 5;
                        if(this.price < 0) {
                            this.price = 0;
                        }
                        return false;
                    }
                },

                checkDisabled: function(e){
                    let value = parseFloat(this.price);

                    if (value < this.minimumDonation || isNaN(value)) {
                        e.preventDefault();
                    }
                }
            },

            // this code is run on creating the vue object and mounting it
            beforeMount() {
                this.sortData();

                // change the URL to include the current group as a query param
                // because we reload the page on changing groups, we only have to do this here
                if(!this.memberships.find(x => x.id == this.currentGroupID)) {
                    this.currentGroupID = this.memberships[0].id;
                    let url = this.getURLOfGroup(this.currentGroupID);
                    window.history.pushState({path: url}, '', url);
                }

                // groups with fixed price tiers don't let you manually set the price
                // so we need to set the price to the applicable membership level
                // instead of using the exact price specified in the query params
                if(this.group.hasFixedPriceTiers) {
                    if(this.price) {
                        for(let i = this.group.levels.length - 1; i >= 0; i--) {
                            let level = this.group.levels[i];

                            if(level.price <= this.price) {
                                this.price = level.price;
                                break;
                            }
                        }
                    } else {
                        this.price = null;
                    }
                }

                // doesn't do anything now, but if we want to support proper
                // browser history support, we can add it here
                // window.addEventListener('onpopstate', event => {
                //     console.log((<any>event).state);
                // });
            },

            mounted() {
                // maybe display a scaffold?
                this.loaded = true;
                this.updateCTABodyClass();

            }
        });
    }
}




// WEBPACK FOOTER //
// ./@modules/views/membership-table-view.ts